import { ComponentChild, createContext } from "preact";
import { createPortal, FC, useCallback, useContext, useEffect, useMemo, useState } from "preact/compat";
import { style } from "typestyle";
import Typography, { TypographyProps } from "./Typography";
import { surfaceSectionNeutralLightest } from "./Colors";

const containerStyle = style({
    position: "absolute",
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
    zIndex: 10,
    pointerEvents: "none",
    boxSizing: "border-box",
});

const toastMargin = 10;

/**
 * A notification popup. You can provide any content to this as a child and it will render it directly with no additional containers except the one used to position it.
 * Use {@link ToastContent} for consistency, which provides a standard rounded box with a background, a message, and the option to provide additional content.
 */
export const Toast: FC<{
    open: boolean;
    onClose: () => void;
    vertical?: "bottom" | "top";
    horizontal?: "center" | "left" | "right";
    timeout?: number;
    children: ComponentChild;
}> = ({ open, onClose, vertical = "bottom", horizontal = "left", timeout = 3000, children }) => {
    const [container, setContainer] = useState<HTMLDivElement | undefined>(undefined);

    useEffect(() => {
        const node = document.createElement("div");
        document.body.appendChild(node);
        node.className = containerStyle;
        setContainer(node);
        return () => {
            document.body.removeChild(node);
            setContainer(undefined);
        };
    }, []);

    useEffect(() => {
        if (!timeout) {
            return;
        }
        const handle = window.setTimeout(onClose, timeout);
        return () => {
            window.clearTimeout(handle);
        };
    }, [onClose, timeout]);

    const boxStyle = useMemo(
        () =>
            style({
                opacity: open ? 1 : 0,
                transition: "all 200ms",
                position: "absolute",
                bottom: vertical === "bottom" ? toastMargin : undefined,
                top: vertical === "top" ? toastMargin : undefined,
                left: horizontal === "left" ? toastMargin : horizontal === "center" ? "50%" : undefined,
                right: horizontal === "right" ? toastMargin : undefined,
                transform:
                    horizontal === "center" ? `translateX(-50%) scale(${open ? 1 : 0})` : `scale(${open ? 1 : 0})`,
            }),
        [horizontal, open, vertical],
    );

    if (!container) {
        return null;
    }

    return createPortal(<div className={boxStyle}>{children}</div>, container);
};

/**
 * For use with {@link Toast}.
 * Provides a standard rounded box with a background, a message, and the option to provide additional content.
 */
export const ToastContent: FC<{ message: string; messageProps?: TypographyProps; children?: ComponentChild }> = ({
    message,
    messageProps,
    children,
}) => {
    const containerStyle = useMemo(
        () =>
            style({
                backgroundColor: surfaceSectionNeutralLightest,
                borderRadius: "4px",
                padding: "13px 15px",
                boxSizing: "border-box",
                boxShadow:
                    "0px 3px 5px -1px rgba(0,0,0,0.2),0px 6px 10px 0px rgba(0,0,0,0.14),0px 1px 18px 0px rgba(0,0,0,0.12)",
                display: "flex",
                flexDirection: "row",
                justifyContent: "space-between",
                minWidth: "200px",
            }),
        [],
    );
    return (
        <div className={containerStyle}>
            <Typography
                {...{
                    variant: "body",
                    size: "base",
                    ...messageProps,
                }}
            >
                {message}
            </Typography>
            {children}
        </div>
    );
};

interface ToastData {
    showToast: (message: string, timeout?: number) => void;
}
const ToastContext = createContext<ToastData>({} as any);

/**
 * A self-managed toast controller. Displays a single toast message. Use {@link useToast} to display messages.
 * This isn't required, but it handles basic use-cases for toasts.
 */
export const ToastProvider: FC<{
    vertical?: "bottom" | "top";
    horizontal?: "center" | "left" | "right";
    children?: ComponentChild;
    timeout?: number;
}> = ({ vertical, horizontal, children, timeout: defaultTimeout }) => {
    const [open, setOpen] = useState(false);
    const [currentText, setCurrentText] = useState("");
    const [currentTimeout, setCurrentTimeout] = useState<number | undefined>(defaultTimeout);

    const showToast = useCallback(
        (message: string, timeout?: number) => {
            setCurrentText(message);
            setOpen(false);
            if (message) {
                setTimeout(() => {
                    setOpen(true);
                    setCurrentTimeout(timeout ?? defaultTimeout);
                }, 100); // Small delay to let the state update
            }
        },
        [defaultTimeout],
    );

    return (
        <ToastContext.Provider value={{ showToast }}>
            <>
                <Toast
                    key={currentText || undefined}
                    vertical={vertical}
                    horizontal={horizontal}
                    open={open}
                    onClose={() => setOpen(false)}
                    timeout={currentTimeout}
                >
                    <ToastContent message={currentText} />
                </Toast>
                {children}
            </>
        </ToastContext.Provider>
    );
};

/**
 * For use with {@link ToastProvider}.
 * @returns An object with the function `showToast`, which you can call to display a message.
 */
export const useToast = () => {
    const ctx = useContext(ToastContext);
    return { showToast: ctx.showToast };
};
